<script lang="ts">
	import { goto } from '$app/navigation';
	import { AccountService, getEmail, getName } from '$lib/service/AccountService';
	import { GameService } from '$lib/service/GameService';
	import { NFTStakingService } from '$lib/service/NftStaking';
	import { copyAdreessToClipboard, genShortAddress, signMessage } from '$lib/service/WalletManager';
	import { rememberMe, selectedAccount, web3Provider } from '$lib/stores/connectstore';
	import { activeGameWallet, activeWallet, keycloak, userInfo } from '$lib/stores/userstore';
	import type {
		Account,
		AddWalletRequest,
		Wallet
	} from '$lib/types';
	import { ga } from '@beyonk/svelte-google-analytics';
	import {
		CONTRACT_ADDRESS,
		DEV_ENV,
		LINK_EXTERNAL_WALLET_MODEL_REMINDER_KEY
	} from '../../../constants';
	import Button from '../button/Button.svelte';
	import SecondaryButton from '../button/SecondaryButton.svelte';
	import CopyIcon from '../icons/CopyIcon.svelte';
	import LinkIcon from '../icons/LinkIcon.svelte';
	import MessageBox from '../model/MessageBox.svelte';
	import Select from '../select/Select.svelte';
	import { notifications } from '../toast/notifications';
	import FontSpan from '../typography/FontSpan.svelte';
	import Web3Modal from '../web3model/Web3Modal.svelte';
	import GameAsset from './GameAsset.svelte';
	import LinkWalletModel from './LinkWalletModel.svelte';
	import LuckuballModel from './LuckuballModel.svelte';

	$: name = getName($userInfo);
	$: email = getEmail($userInfo);

	let wallets: Wallet[] = [];
	async function addWallet(selectedAccount: string | undefined) {
		try {
			if (selectedAccount && $userInfo && $keycloak) {
				let sign = await signMessage($web3Provider, selectedAccount);
				if (sign) {
					const service = new AccountService($keycloak);
					let w: AddWalletRequest = {
						address: selectedAccount,
						active: true,
						userName: $userInfo.email,
						userId: $userInfo.username,
						type: 'external',
						...sign
					};
					await service.addWallets(w);
					notifications.success('Wallet registered for login', 3000);
					ga.addEvent('WEB_LINK_EXTERNAL_WALLET', {
						WALLET_ADDRESS: selectedAccount
					});
					const stakingService = new NFTStakingService($keycloak);
					await stakingService.batchInitOwnerShipOfStakedNFTs();
					await refreshWallets($userInfo);
				}

			}
		} catch (error) {
			notifications.danger('Failed to register for login', 3000);
			wallets = [];
		}
	}

	$: refreshWallets($userInfo);
	async function refreshWallets(userInfo: Account | undefined) {
		if (userInfo && $keycloak) {
			try {
				const service = new AccountService($keycloak);
				wallets = await service.wallets(userInfo.email);
				$activeWallet = wallets.find((w) => w.active && w.type === 'external');

				$activeGameWallet = wallets.find((w) => w.active && w.type === 'internal');
			} catch (error) {
				console.log('failed to load wallet balances: ', error);
				$activeWallet = undefined;
				$activeGameWallet = undefined;
				wallets = [];
			}
		} else {
			// isExternalWalletRegisted = false;
		}
	}

	async function deleteWallet(address: string | undefined) {
		try {
			if (address && $userInfo && $keycloak) {
				const service = new AccountService($keycloak);
				let req = await signMessage($web3Provider, address);
				if (req) {
					await service.deregisterWallet(address, $userInfo.email, req);
					notifications.success('Wallet deregistered successfully', 3000);
					await refreshWallets($userInfo);
					await goto(`/user/wallet`);
				}
			}
		} catch (error) {
			notifications.danger('Failed to deregister wallet', 3000);
		}
	}

	async function handleCombine() {
		// wallet is not connected
		if (!$activeWallet && !$selectedAccount) {
			// let user connect wallet first and on connection proceed with wallet registration
			userWantsToConnect = true;
			combineWalletInProgress = true;
		} else if (!!$selectedAccount) {
			// wallet is connected so add wallet right away
			await addWallet($selectedAccount);
		} else if (!!$activeWallet && !$selectedAccount) {
			// we have one wallet registered so let user connect wallet first
			userWantsToConnect = true;
			combineWalletInProgress = true;
		}
	}

	let openLuckyballs = false;
	function openLuckyballsHandler() {
		openLuckyballs = true;
	}

	let duration = 'All';
	const durationMap = { All: 0, '12H': 0.5, '24H': 1, '05D': 5, '10D': 10 };
	function applyDuration(e: CustomEvent<{ text: string; selectedIndex: number }>) {}

	async function loadGameWalletBalance(_duration: string, _userInfo: Account | undefined) {
		if (_userInfo && $keycloak) {
			const service = new GameService($keycloak);
			return service.loadStakingBalance(_userInfo.email, durationMap[_duration]);
		}
	}

	$: gameWalletBalancePromise = loadGameWalletBalance(duration, $userInfo);

	function getExternalWalletAddress(
		activeWallet: Wallet | undefined,
		selectedAccount: string | undefined
	) {
		if (!!selectedAccount) {
			return genShortAddress(selectedAccount);
		}
		if (!!activeWallet) {
			return genShortAddress(activeWallet.address);
		}
	}

	$: externalShortWalletAddress = getExternalWalletAddress($activeWallet, $selectedAccount);

	let combineWalletInProgress = false;

	function canCombineExternalWallet(
		activeWallet: Wallet | undefined,
		selectedAccount: string | undefined
	) {
		if (
			!!activeWallet &&
			!!selectedAccount &&
			activeWallet.address.toUpperCase() === selectedAccount.toUpperCase()
		) {
			// wallet connected is registered
			return false;
		}
		if (!!activeWallet) {
			const currentTime = new Date().getTime();
			const total = currentTime - activeWallet.updatedTimestamp;
			let days = 0;
			if (DEV_ENV === true) {
				// in dev mode it is 7 seconds.
				days = Math.floor((total / 1000) % 60);
			} else {
				// in prod it is 7 days to change wallet address.
				days = Math.floor(total / (1000 * 60 * 60 * 24));
			}
			if (days < 7) {
				return false;
			}
			return true;
		}
		return true;
	}
	$: canCombine = canCombineExternalWallet($activeWallet, $selectedAccount);

	function showExternalWalletDialog(
		_userInfo: Account | undefined,
		_activeWallet: Wallet | undefined,
		_selectedAccount: string | undefined
	) {
		if (
			_userInfo &&
			typeof _activeWallet === 'undefined' &&
			typeof _selectedAccount === 'undefined'
		) {
			return sessionStorage.getItem(LINK_EXTERNAL_WALLET_MODEL_REMINDER_KEY) !== 'false';
		}
		return false;
	}

	$: linkExternalWallet = showExternalWalletDialog($userInfo, $activeWallet, $selectedAccount);

	let userWantsToConnect = false;

	async function onConnect(e: CustomEvent<{ provider: any }>) {
		userWantsToConnect = false;
		if (combineWalletInProgress) {
			// handle wallet registration after wallet is connected
			if (
				!!$activeWallet &&
				e.detail.provider.selectedAddress.toUpperCase() === $activeWallet.address.toUpperCase()
			) {
				return;
			} else {
				await addWallet(e.detail.provider.selectedAddress);
			}
		}
	}
	let showConflictAccountMsg = false;

	$: console.log('$activeGameWallet', $activeGameWallet?.address);

	let copied = false;

	async function openBlockExplorer(address?: string) {
		if (window?.ethereum?.request && address) {
			const chainId = await window?.ethereum?.request({ method: 'eth_chainId' });
			for (const [key, value] of Object.entries(CONTRACT_ADDRESS)) {
				if (value.CHAIN_ID_HEX === chainId) {
					console.log(`navigating to ${key} network wallet address in block explorer`);
					window.open(`${value.ADD_BLCOK_EXPLORER}/${address}`, '_blank');
					break;
				}
			}
		}
	}
</script>

{#if !!$activeWallet && $selectedAccount !== $activeWallet.address}
	<MessageBox
		open={showConflictAccountMsg}
		title={'Other active wallet present'}
		message={`There is already wallet '${genShortAddress(
			$activeWallet.address
		)}' connected to this account. Adding wallet '${genShortAddress(
			$selectedAccount
		)}' to this account will deactivate wallet '${genShortAddress(
			$activeWallet.address
		)}'. Are you sure you want to continue?`}
	>
		<SecondaryButton
			slot="action"
			text="Connect"
			on:click={async () => {
				showConflictAccountMsg = false;
				await addWallet($selectedAccount);
			}}
		/>
	</MessageBox>
{/if}
{#if userWantsToConnect}
	<Web3Modal
		on:connected={onConnect}
		useCache={true}
		rememberMe={$rememberMe}
		open={userWantsToConnect}
	/>
{/if}
{#if openLuckyballs}
	<LuckuballModel bind:open={openLuckyballs} />
{/if}
<LinkWalletModel
	bind:open={linkExternalWallet}
	on:link={() => handleCombine()}
	on:closed={() => {
		sessionStorage.setItem(LINK_EXTERNAL_WALLET_MODEL_REMINDER_KEY, 'false');
	}}
/>
<div class="flex max-w-[1284px] items-start gap-10 mb-14">
	<div class="flex items-center gap-10 grow self-stretch">
		<div class="player-container">
			<div class="player-image-container" />
			<div class="flex flex-col items-center gap-1 self-stretch">
				<FontSpan varient="body-B-05" class="text-white text-center">{name}</FontSpan>
				<FontSpan varient="body-R-06" class="text-gray-70 text-center">{email}</FontSpan>
			</div>
		</div>
		<div class=" flex flex-col gap-10 items-start py-10">
			<div class="flex flex-col gap-3">
				<div class="flex gap-6">
					<FontSpan varient="body-B-06" class="text-gray-70">Your external wallet</FontSpan>
					{#if canCombine}
						<Button
							class="h-[30px] px-4 text-gray-70"
							text={!!$activeWallet &&
							!!$selectedAccount &&
							$activeWallet?.address.toUpperCase() !== $selectedAccount?.toUpperCase()
								? 'Switch'
								: 'Connect'}
							on:click={() => handleCombine()}
							type="outlined"
						/>
					{/if}
					{#if $activeWallet && $selectedAccount && $activeWallet.address.toUpperCase() === $selectedAccount.toUpperCase()}
						<Button
							class="h-[30px] px-4 text-gray-70"
							text={'Disconnect'}
							on:click={() => deleteWallet($activeWallet?.address)}
							type="outlined"
						/>
					{/if}
				</div>
				{#if externalShortWalletAddress}
					<div class="flex gap-[10px]">
						<FontSpan varient="body-R-03" class="text-gray-40"
							>{externalShortWalletAddress}</FontSpan
						>
						<div class="flex items-end gap-2">
							<CopyIcon
								bind:copied
								on:click={() => {
									copyAdreessToClipboard($selectedAccount);
									copied = true;
									notifications.info('Address copied!', 1000);
									setTimeout(() => {
										copied = false;
									}, 1000);
								}}
							/>
							<LinkIcon
								on:click={() => {
									openBlockExplorer($selectedAccount);
								}}
							/>
						</div>
					</div>
				{/if}
			</div>
		</div>
	</div>
	<div class="right-container">
		<div class="flex flex-col justify-center items-center gap-3">
			<FontSpan varient="body-B-06" class="text-gray-50">
				Total earnings from staking
			</FontSpan>
			<Select
				class="right-container-select w-full !p-2"
				options={['All', '24H', '05D', '10D']}
				bind:selected={duration}
				on:selected={applyDuration}
			/>
		</div>
		<div class="flex flex-col items-start gap-4 self-stretch">
			{#await gameWalletBalancePromise}
				<GameAsset icon="lucky-ball43" label="Lucky Ball" value="loading..." />
				<GameAsset icon="cring43" label="Cring" value="loading..." />
			{:then balance}
				{#if balance}
					<GameAsset
						icon="lucky-ball43"
						label="Lucky Ball"
						value={balance.Luckyball.toLocaleString()}
					/>
					<GameAsset icon="cring43" label="Cring" value={balance.Cring.toLocaleString()} />
				{/if}
			{:catch error}
				<GameAsset icon="lucky-ball43" label="Lucky Ball" value="0,000" />
				<GameAsset icon="cring43" label="Cring" value="0,000" />
			{/await}
		</div>
		<div class="flex justify-center self-stretch">
			<Button on:click={openLuckyballsHandler} type="secondary" class="px-4">
				<FontSpan varient="body-EB-04" class="text-white">Manage Luckyballs</FontSpan>
			</Button>
		</div>
	</div>
</div>

<style>
	:global(.right-container-select) {
		display: flex;
		align-items: center;
		gap: 8px;

		border-radius: 6px;
		border: 1px solid var(--popup-border, #2d2d2d);
		background: var(--bg, #151516) !important;

		/* black_shadow */
		box-shadow: 0px 8px 8px 0px rgba(0, 0, 0, 0.2);

		color: var(--white, #fff);

		/* body-EB-04 */
		font-size: 16px;
		font-family: Lato;
		font-style: normal;
		font-weight: 800;
	}

	.right-container {
		display: flex;
		padding: 32px;
		flex-direction: column;
		align-items: flex-start;
		gap: 28px;
		align-self: stretch;

		border-radius: 10px;
		border: 1px solid var(--popup-border, #2d2d2d);
		background: var(
			--gray-20070,
			linear-gradient(135deg, rgba(57, 57, 57, 0.7) 0%, rgba(40, 40, 40, 0.7) 100%)
		);

		/* black_shadow */
		box-shadow: 0px 8px 8px 0px rgba(0, 0, 0, 0.2);
	}

	.player-image-container {
		width: 128px;
		height: 128px;

		border-radius: 6px;
		background: #49b4b1;

		/* black_shadow */
		box-shadow: 0px 8px 8px 0px rgba(0, 0, 0, 0.2);
	}
	.player-container {
		display: flex;
		padding: 32px;
		flex-direction: column;
		justify-content: center;
		align-items: center;
		gap: 16px;

		border-radius: 10px;
	}
</style>
